import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { X } from "lucide-react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";
import { useAuthModal } from "./AuthModalContext";
import PasswordResetModal from "./PasswordResetModal";
import Turnstile from "react-turnstile";
import { trackEvent, AnalyticsEvents } from "../../utils/analytics";
import { maybeRedirectToWelcome } from "../../constants/productLanes";

export default function AuthModal({ open, onClose }) {
  const [view, setView] = useState("login"); // 'login', 'signup', or 'otp'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Signup fields
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  // Login fields
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  // OTP fields
  const [otp, setOtp] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [isSignupOtp, setIsSignupOtp] = useState(false);
  // Add state for Turnstile tokens
  const [signupTurnstileToken, setSignupTurnstileToken] = useState("");

  const { login: setAuthToken, user } = useAuth();
  const { modalView, setModalView, closeAuthModal, resetEmail } =
    useAuthModal();
  const navigate = useNavigate();
  const location = useLocation();

  const API = import.meta.env.VITE_BACKEND_URL;

  // Reset view to 'login' every time the modal is opened
  useEffect(() => {
    if (open) {
      setView("login");
      setIsSignupOtp(false);
    }
  }, [open]);

  const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/[a-z]/.test(password))
      return "Password must include a lowercase letter.";
    if (!/[A-Z]/.test(password))
      return "Password must include an uppercase letter.";
    if (!/[0-9]/.test(password)) return "Password must include a digit.";
    if (!/[^A-Za-z0-9]/.test(password))
      return "Password must include a symbol.";
    return null;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!signupTurnstileToken) {
      setError("Please complete the CAPTCHA challenge.");
      return;
    }
    const passwordError = validatePassword(signupPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/auth/register`,
        {
          email: signupEmail.trim().toLowerCase(),
          password: signupPassword,
          name: signupName,
          turnstileToken: signupTurnstileToken,
        },
        { withCredentials: true }
      );
      setLoading(false);
      setOtpEmail(signupEmail);
      setIsSignupOtp(true);
      setView("otp");
      setSignupName("");
      setSignupEmail("");
      setSignupPassword("");
      toast.success("Signup successful! Please check your email for the OTP.", {
        autoClose: 10000,
        className: "bg-muted text-foreground",
      });
    } catch (err) {
      setLoading(false);
      setError(
        err.response?.data?.error || "Registration failed. Please try again."
      );
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/api/auth/login`,
        {
          email: loginEmail.trim().toLowerCase(),
          password: loginPassword,
        },
        { withCredentials: true }
      );
      setLoading(false);
      setAuthToken(res.data.token);
      trackEvent(AnalyticsEvents.LOGIN, { method: "email" });
      onClose();
      maybeRedirectToWelcome(navigate, res.data.token, location.pathname);
      setLoginEmail("");
      setLoginPassword("");
      // Parse the JWT to get the user's name
      let name = "";
      try {
        const base64Url = res.data.token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
        );
        name = JSON.parse(jsonPayload).name;
      } catch {}
      toast.success(name ? `Welcome back, ${name}!` : "Welcome back!", {
        autoClose: 5000,
        className: "bg-muted text-foreground",
      });
    } catch (err) {
      setLoading(false);
      const errorMsg =
        err.response?.data?.error || "Login failed. Please try again.";
      setError(errorMsg);
      if (errorMsg.includes("not verified")) {
        setOtpEmail(loginEmail);
        setIsSignupOtp(false);
        setView("otp");
        try {
          await axios.post(`${API}/api/auth/send-otp`, { email: loginEmail });
          toast.info("We’ve sent you a new OTP to verify your account.");
        } catch (sendErr) {
          toast.error("Failed to send OTP. Please try again.");
        }
      }
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/api/auth/verify-otp`,
        {
          email: otpEmail.trim().toLowerCase(),
          otp,
        },
        { withCredentials: true }
      );
      setLoading(false);
      setAuthToken(res.data.token);
      trackEvent(
        isSignupOtp ? AnalyticsEvents.SIGNUP : AnalyticsEvents.LOGIN,
        { method: "email" },
      );
      setIsSignupOtp(false);
      onClose();
      maybeRedirectToWelcome(navigate, res.data.token, location.pathname);
      setOtp("");
      setOtpEmail("");
      toast.success("Account verified and logged in!", {
        autoClose: 5000,
        className: "bg-muted text-foreground",
      });
    } catch (err) {
      setLoading(false);
      setError(
        err.response?.data?.error ||
          "OTP verification failed. Please try again."
      );
    }
  };

  if (!open) return null;

  // Password reset modal logic
  if (modalView === "resetPassword") {
    return (
      <PasswordResetModal
        open={open}
        onClose={() => {
          setModalView("auth");
          closeAuthModal();
        }}
        initialEmail={resetEmail || loginEmail || ""}
      />
    );
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-card rounded-lg p-6 w-full max-w-md max-h-[90vh] flex flex-col mx-4 sm:px-6">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {view === "signup"
                ? "Sign up for PageTest"
                : view === "otp"
                ? "Verify your email"
                : "Sign in to PageTest"}
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <div className="p-0 pt-0 flex-1 overflow-y-auto">
          <div className="mx-1">
            {view === "signup" ? (
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label
                    htmlFor="signup-name"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Name
                  </label>
                  <input
                    id="signup-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Your name"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="signup-email"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Email address
                  </label>
                  <input
                    id="signup-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Your email address"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="signup-password"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Create a Password
                  </label>
                  <div className="relative">
                    <input
                      id="signup-password"
                      name="password"
                      type={showSignupPassword ? "text" : "password"}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring mb-1 pr-10"
                      placeholder="Your password"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                      onClick={() => setShowSignupPassword((v) => !v)}
                      aria-label={
                        showSignupPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showSignupPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>
                <div style={{ width: 200, overflow: "hidden" }}>
                  <Turnstile
                    sitekey="0x4AAAAAABk3IIa_Na1Xi2qg"
                    onSuccess={setSignupTurnstileToken}
                    onExpire={() => setSignupTurnstileToken("")}
                  />
                </div>
                {error && <div className="text-red-600 text-sm">{error}</div>}
                <button
                  type="submit"
                  className="w-full py-2 px-4 bg-foreground text-background rounded-md font-semibold hover:bg-foreground/90 transition-all"
                  disabled={loading || !signupTurnstileToken}
                >
                  {loading ? "Signing up..." : "Sign up"}
                </button>
                <div className="text-center text-sm mt-2">
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="text-brand hover:underline"
                    onClick={() => setView("login")}
                  >
                    Sign in
                  </button>
                </div>
              </form>
            ) : view === "otp" ? (
              <form onSubmit={handleOtpVerify} className="space-y-4">
                <div>
                  <label
                    htmlFor="otp-input"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Enter the OTP sent to your email
                  </label>
                  <input
                    id="otp-input"
                    name="otp"
                    type="text"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                  />
                </div>
                {error && <div className="text-red-600 text-sm">{error}</div>}
                <button
                  type="submit"
                  className="w-full py-2 px-4 bg-foreground text-background rounded-md font-semibold hover:bg-foreground/90 transition-all"
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify"}
                </button>
                <div className="text-center text-sm mt-2">
                  Didn't get the code? Check your spam folder or
                  <button
                    type="button"
                    className="text-brand hover:underline ml-1"
                    onClick={async () => {
                      setLoading(true);
                      setError("");
                      try {
                        await axios.post(`${API}/api/auth/send-otp`, {
                          email: otpEmail,
                        });
                        toast.success("OTP resent! Check your email.");
                      } catch (err) {
                        setError("Failed to resend OTP.");
                      }
                      setLoading(false);
                    }}
                  >
                    resend the code
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label
                    htmlFor="login-email"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Email address
                  </label>
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Your email address"
                    value={loginEmail}
                    onChange={(e) => {
                      setLoginEmail(e.target.value);
                      setError("");
                    }}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="login-password"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="login-password"
                      name="password"
                      type={showLoginPassword ? "text" : "password"}
                      autoComplete="current-password"
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring mb-1 pr-10"
                      placeholder="Your password"
                      value={loginPassword}
                      onChange={(e) => {
                        setLoginPassword(e.target.value);
                        setError("");
                      }}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                      onClick={() => setShowLoginPassword((v) => !v)}
                      aria-label={
                        showLoginPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showLoginPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>
                {error && <div className="text-red-600 text-sm">{error}</div>}

                <button
                  type="submit"
                  className="w-full py-2 px-4 bg-foreground text-background rounded-md font-semibold hover:bg-foreground/90 transition-all disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>
                <div className="text-center text-sm mt-2">
                  <button
                    type="button"
                    className="text-brand hover:underline mr-2"
                    onClick={() => setModalView("resetPassword")}
                  >
                    Forgot password?
                  </button>
                  Don't have an account?{" "}
                  <button
                    type="button"
                    className="text-brand hover:underline"
                    onClick={() => setView("signup")}
                  >
                    Sign up
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
